/* eslint-disable no-continue */
import imageCompression from 'browser-image-compression';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import html2canvas from 'html2canvas';
import moment from 'jalali-moment';
import { getQueryStringsFromLocation, openNewWindow } from 'spec';
import { readFile } from 'xlsx';
// eslint-disable-next-line import/no-cycle
import permissionFunctions from 'authorization/permission-functions';
import navItems from 'components/dashboardItems';
import Num2persian from 'num2persian';
// eslint-disable-next-line import/no-cycle
import { formatMoneyAmount } from '../components/AryNumberFormatter';

// ***********************************************************************
//
// xlsx formatters
//
// ***********************************************************************

export const ExcelFormatterErrorStore = {
  errors: null,

  setErrorsRef(errors) {
    this.errors = errors;
  },

  push(message) {
    if (this.errors) {
      this.errors.push(message);
    }
  }
};

export const toCleanString = (v) => {
  if (v === null || v === undefined) return '';
  return String(v).trim();
};

export const toNumberString = (v) => {
  if (v === null || v === undefined) return '0';
  return String(v).replace(/,/g, '').trim();
};

export const safeDateParse = (date, time) => {
  const clearDate = toCleanString(date);
  const clearTime = toCleanString(time);

  if (!clearDate) return '';

  try {
    const dateAndTime = moment.from(
      `${clearDate} ${clearTime}`,
      'fa',
      'YYYY/M/D HH:mm:ss'
    );

    if (!dateAndTime || !dateAndTime.isValid()) return '';

    return new Date(+dateAndTime).getTime().toString();
  } catch {
    return '';
  }
};

export const parseDateTime = (date, time) => {
  const d = toCleanString(date);
  const t = toCleanString(time);

  if (!d) {
    ExcelFormatterErrorStore.push('date is empty');
    return '';
  }

  try {
    const m = moment.from(`${d} ${t}`, 'fa', 'YYYY/M/D HH:mm:ss');

    if (!m || !m.isValid()) {
      ExcelFormatterErrorStore.push('invalid date format');
      return '';
    }

    return new Date(+m).getTime().toString();
  } catch {
    ExcelFormatterErrorStore.push('date parse failed');
    return '';
  }
};

export const safeParse = (field, fn) => (values) => {
  try {
    const result = fn(values);

    if (result === undefined || result === null) {
      ExcelFormatterErrorStore.push(`Invalid value for ${field}`);
      return '';
    }

    return result;
  } catch (error) {
    ExcelFormatterErrorStore.push(
      `Error parsing ${field}: ${error?.message || error}`
    );
    return '';
  }
};

export const customBankFormatterSettings = {
  row: {
    columns: ['I'],
    parse: safeParse('row', ([v]) => toCleanString(v))
  },

  date: {
    columns: ['G', 'F'],
    parse: safeParse('date', ([date, time]) => parseDateTime(date, time))
  },

  branchCode: {
    columns: ['H'],
    parse: safeParse('branchCode', ([v]) => toCleanString(v))
  },

  transactionType: {
    columns: ['E'],
    parse: safeParse('transactionType', ([v]) => toCleanString(v))
  },

  receiptNumber: {
    columns: ['E'],
    parse: safeParse('receiptNumber', ([v]) => toCleanString(v))
  },

  description: {
    columns: ['D'],
    parse: safeParse('description', ([v]) => toCleanString(v))
  },

  notes: {
    columns: ['D'],
    parse: safeParse('notes', ([v]) => toCleanString(v))
  },

  deposit: {
    columns: ['B'],
    parse: safeParse('deposit', ([v]) => toNumberString(v))
  },

  withdraw: {
    columns: ['C'],
    parse: safeParse('withdraw', ([v]) => toNumberString(v))
  },

  amount: {
    columns: ['B', 'C'],
    parse: safeParse('amount', ([deposit, withdraw]) => {
      const d = toNumberString(deposit);
      const w = toNumberString(withdraw);
      return d !== '0' ? d : w;
    })
  },

  isDeposit: {
    columns: ['B'],
    parse: safeParse('isDeposit', ([deposit]) => {
      const d = toNumberString(deposit);
      return d !== '0';
    })
  },

  balance: {
    columns: ['A'],
    parse: safeParse('balance', ([v]) => toNumberString(v))
  }
};

export const saderatFormatterSettings = {
  row: {
    columns: ['A'],
    parse: safeParse('row', ([v]) => toCleanString(v))
  },

  date: {
    columns: ['B', 'C'],
    parse: safeParse('date', ([date, time]) => parseDateTime(date, time))
  },

  branchCode: {
    columns: ['D'],
    parse: safeParse('branchCode', ([v]) => toCleanString(v))
  },

  transactionType: {
    columns: ['E'],
    parse: safeParse('transactionType', ([v]) => toCleanString(v))
  },

  receiptNumber: {
    columns: ['F'],
    parse: safeParse('receiptNumber', ([v]) =>
      toCleanString(v).replace(/ /g, '')
    )
  },

  description: {
    columns: ['G'],
    parse: safeParse('description', ([v]) => toCleanString(v))
  },

  notes: {
    columns: ['E', 'G'],
    parse: safeParse(
      'notes',
      ([type, desc]) => `${toCleanString(type)} - ${toCleanString(desc)}`
    )
  },

  deposit: {
    columns: ['H'],
    parse: safeParse('deposit', ([v]) => toNumberString(v))
  },

  withdraw: {
    columns: ['I'],
    parse: safeParse('withdraw', ([v]) => toNumberString(v))
  },

  amount: {
    columns: ['H', 'I'],
    parse: safeParse('amount', ([deposit, withdraw]) => {
      const d = toNumberString(deposit);
      const w = toNumberString(withdraw);
      return d !== '0' ? d : w;
    })
  },

  isDeposit: {
    columns: ['H'],
    parse: safeParse('isDeposit', ([deposit]) => {
      const d = toNumberString(deposit);
      return d !== '0';
    })
  },

  balance: {
    columns: ['J'],
    parse: safeParse('balance', ([v]) => toNumberString(v))
  }
};

export const saderatNewFormatterSettings = {
  row: {
    columns: ['A'],
    parse: safeParse('row', ([v]) => toCleanString(v))
  },

  date: {
    columns: ['B', 'C'],
    parse: safeParse('date', ([date, time]) => safeDateParse(date, time))
  },

  branchCode: {
    columns: ['E'],
    parse: safeParse('branchCode', ([v]) => toCleanString(v))
  },

  transactionType: {
    columns: ['D'],
    parse: safeParse('transactionType', ([v]) => toCleanString(v))
  },

  receiptNumber: {
    columns: ['G'],
    parse: safeParse('receiptNumber', ([v]) =>
      toCleanString(v).replace(/ /g, '')
    )
  },

  description: {
    columns: ['K'],
    parse: safeParse('description', ([v]) => toCleanString(v))
  },

  notes: {
    columns: ['K'],
    parse: safeParse('notes', ([v]) => toCleanString(v))
  },

  deposit: {
    columns: ['I'],
    parse: safeParse('deposit', ([v]) => toNumberString(v))
  },

  withdraw: {
    columns: ['H'],
    parse: safeParse('withdraw', ([v]) => toNumberString(v))
  },

  amount: {
    columns: ['I', 'H'],
    parse: safeParse('amount', ([deposit, withdraw]) => {
      const d = toNumberString(deposit);
      const w = toNumberString(withdraw);
      return d !== '0' ? d : w;
    })
  },

  isDeposit: {
    columns: ['I'],
    parse: safeParse('isDeposit', ([deposit]) => {
      const d = toNumberString(deposit);
      return d !== '0';
    })
  },

  balance: {
    columns: ['J'],
    parse: safeParse('balance', ([v]) => toNumberString(v))
  }
};

export const sepahFormatterSettings = {
  row: {
    columns: ['AG'],
    parse: safeParse('row', ([v]) => toCleanString(v))
  },

  date: {
    columns: ['AC', 'AA'],
    parse: safeParse('date', ([date, time]) => safeDateParse(date, time))
  },

  branchCode: {
    columns: ['AF'],
    parse: safeParse('branchCode', ([v]) => toCleanString(v))
  },

  transactionType: {
    columns: ['E'],
    parse: safeParse('transactionType', ([v]) => toCleanString(v))
  },

  receiptNumber: {
    columns: ['Y', 'Z'],
    parse: safeParse('receiptNumber', ([y, z]) =>
      `${toCleanString(y)}${toCleanString(z)}`.replace(/ /g, '')
    )
  },

  description: {
    columns: ['H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R'],
    parse: safeParse('description', (values) =>
      values.map(toCleanString).join(' ').trim()
    )
  },

  notes: {
    columns: ['H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R'],
    parse: safeParse('notes', (values) =>
      values.map(toCleanString).join(' ').trim()
    )
  },

  deposit: {
    columns: ['D', 'E', 'F'],
    parse: safeParse('deposit', ([d, e, f]) => toNumberString(d || e || f))
  },

  withdraw: {
    columns: ['G'],
    parse: safeParse('withdraw', ([v]) => toNumberString(v))
  },

  amount: {
    columns: ['D', 'G'],
    parse: safeParse('amount', ([deposit, withdraw]) => {
      const d = toNumberString(deposit);
      const w = toNumberString(withdraw);
      return d !== '0' ? d : w;
    })
  },

  isDeposit: {
    columns: ['D'],
    parse: safeParse('isDeposit', ([deposit]) => {
      const d = toNumberString(deposit);
      return d !== '0';
    })
  },

  balance: {
    columns: ['C'],
    parse: safeParse('balance', ([v]) => toNumberString(v))
  }
};

let getUserCurrenciesQueryDataList = null;
export const useCustomerListFormatterSetting = () => {
  const { t } = useFrameworkTranslation();

  return {
    row: {
      columns: ['A'],
      parse: ([a]) => a
    },
    accountTitle: {
      columns: ['B'],
      parse: ([accountTitle], rowNumber, errors) => {
        const accountTitleType = typeof accountTitle;
        let parsedAccountTitle = '';

        switch (accountTitleType) {
          case 'string':
            parsedAccountTitle = accountTitle.trim();
            break;
          case 'number':
            parsedAccountTitle = accountTitle.toString().trim();
            break;
          default:
            break;
        }
        if (parsedAccountTitle === '') {
          CustomerListExcelFileErrorGenerator(
            parsedAccountTitle,
            rowNumber,
            t('utils.accountCode'),
            errors,
            t
          );
        }
        return parsedAccountTitle;
      }
    },
    firstName: {
      columns: ['C'],
      parse: ([firstName], rowNumber, errors) => {
        const FirstNameType = typeof firstName;
        let parsedFirstName = '';

        switch (FirstNameType) {
          case 'string':
            parsedFirstName = firstName.trim();
            break;
          case 'number':
            parsedFirstName = firstName.toString().trim();
            break;
          default:
            break;
        }
        if (parsedFirstName === '') {
          CustomerListExcelFileErrorGenerator(
            parsedFirstName,
            rowNumber,
            t('utils.name'),
            errors,
            t
          );
        }
        return parsedFirstName;
      }
    },
    lastName: {
      columns: ['D'],
      parse: ([lastName], rowNumber, errors) => {
        const lastNameType = typeof lastName;
        let parsedLastName = '';

        switch (lastNameType) {
          case 'string':
            parsedLastName = lastName.trim();
            break;
          case 'number':
            parsedLastName = lastName.toString().trim();
            break;
          default:
            break;
        }
        if (parsedLastName === '') {
          CustomerListExcelFileErrorGenerator(
            parsedLastName,
            rowNumber,
            t('utils.lastName'),
            errors,
            t
          );
        }
        return parsedLastName;
      }
    },
    fatherName: {
      columns: ['E'],
      parse: ([fatherName]) => {
        const fatherNameType = typeof fatherName;
        let parsedFatherName = '';

        switch (fatherNameType) {
          case 'string':
            parsedFatherName = fatherName.trim();
            break;
          case 'number':
            parsedFatherName = fatherName.toString().trim();
            break;
          default:
            parsedFatherName = '';
            break;
        }
        return parsedFatherName;
      }
    },
    phoneNumber: {
      columns: ['F'],
      parse: ([phoneNumber], rowNumber, errors) => {
        const phoneNumberType = typeof phoneNumber;
        let parsedPhoneNumber = '';

        switch (phoneNumberType) {
          case 'string':
            parsedPhoneNumber = phoneNumber.trim();
            break;
          case 'number':
            parsedPhoneNumber = phoneNumber.toString().trim();
            break;
          default:
            parsedPhoneNumber = '';
            break;
        }
        if (
          parsedPhoneNumber.length > 15 ||
          (parsedPhoneNumber.length < 6 && parsedPhoneNumber.length > 0)
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedPhoneNumber,
            rowNumber,
            t('utils.phoneNumber'),
            errors,
            t
          );
        } else if (
          !/^\d+$/.test(parsedPhoneNumber) &&
          parsedPhoneNumber.length > 0
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedPhoneNumber,
            rowNumber,
            t('utils.phoneNumber'),
            errors,
            t
          );
        }
        return parsedPhoneNumber;
      }
    },
    countryCode: {
      columns: ['G'],
      parse: ([countryCode], rowNumber, errors) => {
        const countryCodeType = typeof countryCode;
        let parsedCountryCode = '';

        switch (countryCodeType) {
          case 'string':
            parsedCountryCode = countryCode.trim();
            break;
          case 'number':
            parsedCountryCode = countryCode.toString().trim();
            break;
          default:
            parsedCountryCode = '';
            break;
        }
        if (
          parsedCountryCode !== '' &&
          (parsedCountryCode.length > 4 || parsedCountryCode.length < 2)
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedCountryCode,
            rowNumber,
            t('utils.countryCode'),
            errors,
            t
          );
        }
        return parsedCountryCode;
      }
    },
    amount: {
      columns: ['H'],
      parse: ([amount], rowNumber, errors) => {
        const amountType = typeof amount;
        let parsedAmount = '';

        switch (amountType) {
          case 'string':
            parsedAmount = amount.trim();
            break;
          case 'number':
            parsedAmount = amount.toString().trim();
            break;
          default:
            parsedAmount = '';
            break;
        }
        if (parsedAmount !== '' && !Number(parsedAmount)) {
          CustomerListExcelFileErrorGenerator(
            parsedAmount,
            rowNumber,
            t('utils.inputAmount'),
            errors,
            t
          );
        }
        return parsedAmount;
      }
    },
    currencyCode: {
      columns: ['I'],
      parse: ([currencyCode], rowNumber, errors) => {
        const currencyCodeType = typeof currencyCode;
        let parsedCurrencyCode = '';

        switch (currencyCodeType) {
          case 'string':
            parsedCurrencyCode = currencyCode.trim();
            break;
          case 'number':
            parsedCurrencyCode = currencyCode.toString().trim();
            break;
          default:
            parsedCurrencyCode = '';
            break;
        }

        const foundCurrency = getUserCurrenciesQueryDataList.find(
          (currency) => currency.title.trim() === parsedCurrencyCode.trim()
        );

        if (!foundCurrency && parsedCurrencyCode !== '') {
          errors.push(
            t('utils.currencyNotIncluded', {
              currencyCode: parsedCurrencyCode,
              rowNumber: rowNumber - 13
            })
          );
          return {
            code: null,
            title: null
          };
        }

        return {
          code: foundCurrency ? foundCurrency.code : '',
          title: foundCurrency ? foundCurrency.title.trim() : ''
        };
      }
    },
    isDebtor: {
      columns: ['J'],
      parse: ([isDebtor], rowNumber, errors) => {
        const isDebtorType = typeof isDebtor;
        let parsedIsDebtor = '';

        switch (isDebtorType) {
          case 'string':
            parsedIsDebtor = isDebtor.trim();
            break;
          case 'number':
            parsedIsDebtor = isDebtor.toString().trim();
            break;
          default:
            parsedIsDebtor = false;
            break;
        }
        if (
          parsedIsDebtor !== 'بدهکار' &&
          parsedIsDebtor !== 'طلبکار' &&
          parsedIsDebtor !== ''
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedIsDebtor,
            rowNumber,
            t('utils.debtorCreditor'),
            errors,
            t
          );
        } else if (parsedIsDebtor === 'بدهکار') {
          parsedIsDebtor = true;
        } else if (parsedIsDebtor === 'طلبکار') {
          parsedIsDebtor = false;
        }
        return parsedIsDebtor;
      }
    },
    gender: {
      columns: ['K'],
      parse: ([gender], rowNumber, errors) => {
        const genderType = typeof gender;
        let parsedGender = '';

        switch (genderType) {
          case 'string':
            parsedGender = gender.trim();
            break;
          case 'number':
            parsedGender = gender.toString().trim();
            break;
          default:
            break;
        }
        if (
          parsedGender !== 'مرد' &&
          parsedGender !== 'زن' &&
          parsedGender !== 'نامعلوم'
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedGender,
            rowNumber,
            t('utils.gender'),
            errors,
            t
          );
        } else if (parsedGender === 'مرد') {
          parsedGender = 1;
        } else if (parsedGender === 'زن') {
          parsedGender = 2;
        } else if (parsedGender === 'نامعلوم') {
          parsedGender = 3;
        }
        return parsedGender;
      }
    },
    groupCode: {
      columns: ['L'],
      parse: ([groupCode], rowNumber, errors) => {
        const groupCodeType = typeof groupCode;
        let parsedGroupCode = '';

        switch (groupCodeType) {
          case 'string':
            parsedGroupCode = groupCode.trim();
            break;
          case 'number':
            parsedGroupCode = groupCode.toString().trim();
            break;
          default:
            break;
        }
        if (
          parsedGroupCode !== t('General') &&
          parsedGroupCode !== t('Banking') &&
          parsedGroupCode !== t('Cost')
        ) {
          CustomerListExcelFileErrorGenerator(
            parsedGroupCode,
            rowNumber,
            t('Group'),
            errors,
            t
          );
        } else if (parsedGroupCode === t('General')) {
          parsedGroupCode = 0;
        } else if (parsedGroupCode === t('Banking')) {
          parsedGroupCode = 1;
        } else if (parsedGroupCode === t('Cost')) {
          parsedGroupCode = 2;
        }
        return parsedGroupCode;
      }
    }
  };
  // personCategory: {
  //   columns: ['M'],
  //   parse: ([personCategory], rowNumber, errors) => {
  //     const personCategoryType = typeof personCategory;
  //     let parsedPersonCategory = '';

  //     switch (personCategoryType) {
  //       case 'string':
  //         parsedPersonCategory = personCategory.trim();
  //         break;
  //       case 'number':
  //         parsedPersonCategory = personCategory.toString().trim();
  //         break;
  //       default:
  //         break;
  //     }
  //     if (
  //       parsedPersonCategory !== 'مشتری' &&
  //       parsedPersonCategory !== 'گیرنده'
  //     ) {
  //       CustomerListExcelFileErrorGenerator(
  //         parsedPersonCategory,
  //         rowNumber,
  //         'دسته بندی',
  //         errors
  //       );
  //     } else if (parsedPersonCategory === 'مشتری') {
  //       parsedPersonCategory = 1;
  //     } else if (parsedPersonCategory === 'گیرنده') {
  //       parsedPersonCategory = 2;
  //     }
  //     return parsedPersonCategory;
  //   }
  // }
};
// ***********************************************************************
//
// date functions
//
// ***********************************************************************

export function getMoonUtcTime(date) {
  if (date === null) return null;
  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
    0
  ).toString();
}

export function getTimezoneBasedNightTime(myDate) {
  if (myDate === null) return null;
  const date = new Date(myDate);
  date.setFullYear(myDate.getFullYear());
  date.setMonth(myDate.getMonth());
  date.setDate(myDate.getDate());
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  return date.getTime().toString();
}

export function getUtcTime(date, seconds) {
  if (date === null) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    seconds || 0,
    0
  )
    .getTime()
    .toString();
}

export function getTimeZoneFormattedDate(
  date,
  isGregorian,
  timeZone,
  isDigitsLatin
) {
  const locale = isGregorian ? 'en-ZA' : 'fa-IR';
  const numberingSystem = isDigitsLatin ? 'latn' : null;
  const localeDateString = new Date(Number(date)).toLocaleDateString(locale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    numberingSystem
  });
  return localeDateString;
}

export function getTimeZoneFormattedTime(
  date,
  isGregorian = false,
  timeZone = 'Asia/Tehran',
  isDigitsLatin = true
) {
  const formattedDate = getTimeZoneFormattedDate(
    date,
    isGregorian,
    timeZone,
    isDigitsLatin
  );
  const locale = isGregorian ? 'en-ZA' : 'fa-IR';
  const numberingSystem = isDigitsLatin ? 'latn' : undefined;
  const formattedTime = new Date(+date).toLocaleTimeString(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    numberingSystem
  });
  return `${formattedDate} - ${formattedTime}`;
}

export function getRemainingDays(startDate, endDate) {
  if (endDate == null) return null;

  const start = new Date(+startDate);
  const end = new Date(+endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((end - start) / msPerDay);
}

export function getPastOrFutureYear(yearCount, date = new Date().getTime()) {
  const year = new Date(date).Year;
  const dayCount = year % 4 === 0 ? 366 : 365;
  const dayTimeStamp = 24 * 3600 * 1000;
  return date + yearCount * dayCount * dayTimeStamp;
}

export function getMonthName(date, isGregorian, timeZone) {
  const locale = isGregorian ? 'en-US' : 'fa-IR';
  return new Date(+date).toLocaleTimeString(locale, {
    timeZone,
    month: 'long'
  });
}

// ***********************************************************************
//
// function for convert persian and arabic numbers to english
//
// ***********************************************************************

export function fixNumbers(input) {
  return input;
  // const str = input.toString();
  // let finalStr;
  // const persianNumbers = [
  //   /۰/g,
  //   /۱/g,
  //   /۲/g,
  //   /۳/g,
  //   /۴/g,
  //   /۵/g,
  //   /۶/g,
  //   /۷/g,
  //   /۸/g,
  //   /۹/g
  // ];
  // const arabicNumbers = [
  //   /٠/g,
  //   /١/g,
  //   /٢/g,
  //   /٣/g,
  //   /٤/g,
  //   /٥/g,
  //   /٦/g,
  //   /٧/g,
  //   /٨/g,
  //   /٩/g
  // ];
  // for (let i = 0; i < 10; i++) {
  //   finalStr = str.replace(persianNumbers[i], i).replace(arabicNumbers[i], i);
  // }
  // return finalStr;
}

// ***********************************************************************
//
// file compressor
//
// ***********************************************************************

export async function compressFile(fileOrBlob) {
  try {
    if (
      fileOrBlob.type !== 'application/pdf' &&
      fileOrBlob.type !== 'application/msword' &&
      fileOrBlob.type !==
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
      fileOrBlob.type !== 'application/vnd.ms-excel' &&
      fileOrBlob.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      const compressedBlob = await imageCompression(fileOrBlob, {
        initialQuality: 0.3
      });
      return new File([compressedBlob], compressedBlob.name, {
        type: compressedBlob.type
      });
    }
    return fileOrBlob;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function compressFileForProfile(fileOrBlob, maxSizeInKB = 3) {
  try {
    if (
      fileOrBlob.type !== 'application/pdf' &&
      fileOrBlob.type !== 'application/msword' &&
      fileOrBlob.type !==
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
      fileOrBlob.type !== 'application/vnd.ms-excel' &&
      fileOrBlob.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      const compressedBlob = await imageCompression(fileOrBlob, {
        initialQuality: 0.3,
        maxSizeMB: maxSizeInKB / 1024
      });
      return new File([compressedBlob], compressedBlob.name, {
        type: compressedBlob.type
      });
    }
    return fileOrBlob;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function compressFiles(filesBlob) {
  try {
    const compressedFilesPromises = [];
    for (let i = 0; i < filesBlob.length; i++) {
      compressedFilesPromises.push(compressFile(filesBlob[i]));
    }
    const compressedFileBlobs = await Promise.all(compressedFilesPromises);
    const compressedFiles = compressedFileBlobs.map(
      (blob) => new File([blob], blob.name, { type: blob.type })
    );
    return compressedFiles;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function getImageSrc(fileOrBlobOrBase64) {
  if (!fileOrBlobOrBase64) return null;

  const base64regex =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

  if (base64regex.test(fileOrBlobOrBase64)) {
    return `data:image/png;base64,${fileOrBlobOrBase64}`;
  }
  const objectURL = URL.createObjectURL(fileOrBlobOrBase64);
  return objectURL;
}

export function getBlobImage(fileBlob) {
  const objectURL = URL.createObjectURL(fileBlob);
  const myImage = new Image();
  myImage.src = objectURL;
  return myImage;
}

// ***********************************************************************
//
// file functions
//
// ***********************************************************************

export function getFileNameFromFileId(fileId) {
  const splittedArray = fileId.split('_');
  splittedArray.shift();
  return splittedArray.join('_');
}

export function downloadFileInBrowser(blob, fileName) {
  const file = new File([blob], fileName, {
    type: blob.type
  });
  const objectURL = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = objectURL;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ***********************************************************************
//
// functions for strings
//
// ***********************************************************************

export function limitCharacters(text, maxChar) {
  let newText = text.trim();
  if (newText.length <= maxChar) return newText;

  newText = newText.substr(0, maxChar + 1);
  newText = newText.substr(0, newText.lastIndexOf(' '));
  return `${newText}...`;
}

export function translatePhraseItem(translate, phraseItem) {
  if (!phraseItem || !translate) return null;
  const finalText = phraseItem.shouldTranslate
    ? translate(phraseItem.text.toLowerCase().trim())
    : phraseItem.text;
  return finalText;
}

export function generateTransactionDescription(translate, tdArr) {
  let td = '';
  tdArr?.forEach((element) => {
    td = td.concat(translatePhraseItem(translate, element));
    td = td.concat(' ');
  });
  return td.trim();
}

// ***********************************************************************
//
// xlsx functions
//
// ***********************************************************************

export function useGetDataFromExcelFile() {
  const { t } = useFrameworkTranslation();
  const getDataFromExcelFile = (file, formatter, primaryCurrency) => {
    const workbook = readFile(file);
    const numberOfSheets = workbook.SheetNames.length;
    const transactions = [];
    const errors = [];

    const getExcelFileFirstRow = (worksheet, columnOfRow) => {
      try {
        for (const z in worksheet) {
          if (
            z.toString().substring(0, columnOfRow[0].length) === columnOfRow[0]
          ) {
            if (worksheet[z].v === '1' || worksheet[z].v === 1) {
              return parseInt(z.substring(columnOfRow[0].length));
            }
          }
        }
        errors.push(t('utils.firstRowEmpty'));
        return null;
      } catch (error) {
        errors.push(`Error in finding first row: ${error}`);
        return null;
      }
    };

    const getExcelFileLastRow = (worksheet, firstRow, columnOfRow) => {
      try {
        let lastRow = 0;
        for (const z in worksheet) {
          if (
            z.toString().substring(0, columnOfRow[0].length) === columnOfRow[0]
          ) {
            lastRow = z.toString().substring(columnOfRow[0].length);
          }
        }
        if (lastRow === 0) {
          errors.push(t('utils.lastRowNotFound'));
          return null;
        }
        return parseInt(lastRow);
      } catch (error) {
        errors.push(`Error in finding last row: ${error}`);
        return null;
      }
    };

    for (
      let currentWorksheet = 0;
      currentWorksheet < numberOfSheets;
      currentWorksheet++
    ) {
      const worksheet = workbook.Sheets[workbook.SheetNames[currentWorksheet]];

      const firstRow = getExcelFileFirstRow(worksheet, formatter.row.columns);
      const lastRow = getExcelFileLastRow(
        worksheet,
        firstRow,
        formatter.row.columns
      );
      if (!firstRow || !lastRow || firstRow > lastRow) {
        continue;
      }

      for (let currentRow = firstRow; currentRow <= lastRow; currentRow++) {
        const firstCell = worksheet[formatter.row.columns[0] + currentRow];

        if (!firstCell || !firstCell.v || !/^\d/.test(String(firstCell.v))) {
          continue;
        }

        const dataKeys = Object.keys(formatter);
        const transaction = {};
        let rowHasError = false;

        for (let index = 0; index < dataKeys.length; index++) {
          const key = dataKeys[index];

          const worksheetResult = formatter[key].columns.map((column) => {
            const cell = worksheet[column + currentRow];
            return cell ? cell.v : null;
          });

          try {
            const value = formatter[key].parse(worksheetResult);
            transaction[key] = value;
          } catch (parseError) {
            console.error(
              `Error parsing field ${key} at row ${currentRow}:`,
              parseError
            );
            rowHasError = true;
          }
        }

        if (!rowHasError) {
          if (primaryCurrency === 'IRT') {
            if (typeof transaction.amount === 'string') {
              transaction.amount = parseFloat(transaction.amount);
            }
            if (!Number.isNaN(transaction.amount)) {
              transaction.amount /= 10;
            }
          }

          delete transaction.row;
          delete transaction.transactionType;
          delete transaction.deposit;
          delete transaction.withdraw;
          delete transaction.balance;
          delete transaction.description;

          transactions.push(transaction);
        }
      }
    }
    return { transactions, errors };
  };
  return getDataFromExcelFile;
}
export function useGetDataFromCustomerExcelFile() {
  const { t } = useFrameworkTranslation();

  const getDataFromCustomerExcelFile = (
    file,
    formatter,
    getUserCurrenciesQueryData
  ) => {
    const workbook = readFile(file);
    const numberOfSheets = workbook.SheetNames.length;
    const customers = [];
    const errors = [];

    // eslint-disable-next-line consistent-return
    const getExcelFileFirstRow = (worksheet, columnOfRow) => {
      let firstRow;
      try {
        for (const z in worksheet) {
          if (
            z.toString().substring(0, columnOfRow[0].length) === columnOfRow[0]
          ) {
            if (worksheet[z].v === '1' || worksheet[z].v === 1) {
              firstRow = parseInt(z.substring(columnOfRow[0].length));
              return firstRow;
            }
          }
        }
        if (!firstRow) {
          errors.push('ردیف اول نمیتواند خالی باشد');
        }
        return null;
      } catch (error) {
        errors.push(`Error: ${error}`);
      }
    };

    // eslint-disable-next-line consistent-return
    const getExcelFileLastRow = (worksheet, firstRow, columnOfRow) => {
      try {
        let lastRow = 0;
        for (const z in worksheet) {
          if (
            z.toString().substring(0, columnOfRow[0].length) === columnOfRow[0]
          ) {
            lastRow = z.toString().substring(columnOfRow[0].length);
          }
        }
        if (lastRow === 0) {
          errors.push('ردیف آخر شناسایی نشد');
        }
        return lastRow;
      } catch (error) {
        errors.push(`Error: ${error}`);
      }
    };

    getUserCurrenciesQueryDataList = getUserCurrenciesQueryData;

    for (
      let currentWorksheet = 0;
      currentWorksheet < numberOfSheets;
      currentWorksheet++
    ) {
      const worksheet = workbook.Sheets[workbook.SheetNames[currentWorksheet]];
      const firstRow = getExcelFileFirstRow(worksheet, formatter.row.columns);
      const lastRow = getExcelFileLastRow(
        worksheet,
        firstRow,
        formatter.row.columns
      );

      for (let currentRow = firstRow; currentRow <= lastRow; currentRow++) {
        const firstCell = worksheet[formatter.row.columns[0] + currentRow];
        if (!firstCell || !firstCell.v || /^\s*$/.test(firstCell.v)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const dataKeys = Object.keys(formatter);
        const customer = {};
        for (let index = 0; index < dataKeys.length; index++) {
          const key = dataKeys[index];
          const column = formatter[key].columns[0];
          const currentCell = worksheet[column + currentRow];
          const worksheetResult = currentCell ? currentCell.v : '';
          const value = formatter[key].parse(
            [worksheetResult],
            currentRow,
            errors
          );
          customer[key] = value;
        }
        customers.push(customer);
      }
    }

    for (let i = 0; i < customers.length; i++) {
      for (let j = i + 1; j < customers.length; j++) {
        if (customers[i].accountTitle === customers[j].accountTitle) {
          if (customers[i].firstName !== customers[j].firstName) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentName', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].lastName !== customers[j].lastName) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentLastName', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].fatherName !== customers[j].fatherName) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentFatherName', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].phoneNumber !== customers[j].phoneNumber) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentPhone', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (
            customers[i].currencyCode.code === customers[j].currencyCode.code
          ) {
            errors.push(
              t('utils.rowsWithSameAccountSameCurrency', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].countryCode !== customers[j].countryCode) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentCountry', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].gender !== customers[j].gender) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentGender', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (customers[i].groupCode !== customers[j].groupCode) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentGroup', {
                accountTitle: customers[i].accountTitle
              })
            );
          } else if (
            customers[i].personCategory !== customers[j].personCategory
          ) {
            errors.push(
              t('utils.rowsWithSameAccountDifferentCategory', {
                accountTitle: customers[i].accountTitle
              })
            );
          }
        } else if (customers[i].accountTitle !== customers[j].accountTitle) {
          if (
            customers[i].phoneNumber !== '' &&
            customers[j].phoneNumber &&
            customers[i].phoneNumber === customers[j].phoneNumber
          ) {
            errors.push(
              t('utils.differentAccountsSamePhone', {
                account1: customers[i].accountTitle,
                account2: customers[j].accountTitle
              })
            );
          }
        }
      }
    }

    for (const customer of customers) {
      const invalidCustomer =
        (!customer.currencyCode.code &&
          !customer.amount &&
          !customer.isDebtor?.toString()) ||
        (customer.currencyCode.code &&
          customer.amount &&
          customer.isDebtor?.toString());
      const validCustomerPhoneNumber =
        (customer.phoneNumber && customer.countryCode) ||
        (!customer.phoneNumber && !customer.countryCode);

      if (!invalidCustomer) {
        errors.push(t('utils.currencyAmountDebtorCreditorRequired'));
      } else if (!validCustomerPhoneNumber) {
        errors.push(t('utils.phoneCountryCodeRequired'));
      }
      // const validCustomerCurrency =
      //   (customer.currencyCode &&
      //     customer.amount &&
      //     !!customer.isDebtor?.toString()) ||
      //   (!customer.currencyCode ||
      //     !customer.amount ||
      //     !customer.isDebtor?.toString());
      // const invalidCustomer =
      //   !customer.currencyCode.code ||
      //   !customer.amount ||
      //   !customer.isDebtor?.toString();

      // if (invalidCustomer) {
      //   errors.push(
      //     'در هر ردیف، ارز-مبلغ-طلبکار/بدهکار یا همگی باید پر باشند یا همگی خالی باشند.'
      //   );
      // }
    }

    return { customers, errors };
  };

  return getDataFromCustomerExcelFile;
}
// ***********************************************************************
//
// calculation of source and destination
// currencies according to their dollar rate
//
// ***********************************************************************

export function getSourceToDestRate(
  sourceDollarRate,
  sourceIsDivision,
  destinationDollarRate,
  destinationIsDivision
) {
  const sourceToDollarRate = sourceIsDivision
    ? 1 / sourceDollarRate
    : sourceDollarRate;
  const destinationToDollarRate = destinationIsDivision
    ? 1 / destinationDollarRate
    : destinationDollarRate;
  return sourceToDollarRate / destinationToDollarRate;
}

export function convertCalculationRateToSourceAndDestinationAmounts(
  calculationRate,
  sourceToDestRate
) {
  const sourceAmount = 10 ** calculationRate;
  return { sourceAmount, destinationAmount: sourceToDestRate * sourceAmount };
}

// ***********************************************************************
//
// calculation of tertiary components
//
// ***********************************************************************

export function calculateFinalAmount(
  tradeAmount,
  tradeRate,
  calcRate,
  isDivision,
  initialCalculation = true
) {
  if (isDivision && tradeRate && initialCalculation) {
    return (tradeAmount * 10 ** calcRate) / tradeRate;
  }
  if (!isDivision && calcRate !== undefined && initialCalculation) {
    return (tradeAmount * tradeRate) / 10 ** calcRate;
  }
  return null;
}

export function calculateTradeRate(
  tradeAmount,
  finalAmount,
  calcRate,
  isDivision
) {
  if (isDivision && finalAmount) {
    return (tradeAmount * 10 ** calcRate) / finalAmount;
  }
  if (!isDivision && tradeAmount) {
    return (finalAmount * 10 ** calcRate) / tradeAmount;
  }
  return null;
}

export function calculateTradeAmount(
  tradeRate,
  finalAmount,
  calcRate,
  isDivision
) {
  if (isDivision && calcRate !== undefined) {
    return (finalAmount * tradeRate) / 10 ** calcRate;
  }
  if (!isDivision && tradeRate) {
    return (finalAmount * 10 ** calcRate) / tradeRate;
  }
  return null;
}

export function handleChangeFirstTertiaryComponent({
  lockedLabel,
  setValues,
  numberValue,
  initial,
  tradeRate,
  result,
  calculationRate,
  isDivision,
  initialCalculation = true
}) {
  if (!lockedLabel) {
    setValues({ [initial.name]: numberValue });
  } else if (lockedLabel === tradeRate.lockedLabel) {
    const resultAmount = calculateFinalAmount(
      numberValue,
      tradeRate.amount,
      calculationRate,
      isDivision,
      initialCalculation
    );
    if (resultAmount < 10000000000000) {
      setValues({
        [initial.name]: numberValue,
        [result.name]: resultAmount || ''
      });
    } else if (resultAmount > 10000000000000) {
      setValues({ [initial.name]: numberValue });
    }
  } else if (lockedLabel === result.lockedLabel) {
    const rateAmount = calculateTradeRate(
      numberValue,
      result.amount,
      calculationRate,
      isDivision
    );
    setValues({
      [initial.name]: numberValue,
      [tradeRate.name]: rateAmount || ''
    });
  }
}

export function handleChangeSecondTertiaryComponent({
  lockedLabel,
  setValues,
  numberValue,
  initial,
  tradeRate,
  result,
  calculationRate,
  isDivision,
  initialCalculation = true
}) {
  if (!lockedLabel) {
    setValues({ [tradeRate.name]: numberValue });
  } else if (lockedLabel === initial.lockedLabel) {
    const resultAmount = calculateFinalAmount(
      initial.amount,
      numberValue,
      calculationRate,
      isDivision,
      initialCalculation
    );
    if (resultAmount < 10000000000000) {
      setValues({
        [tradeRate.name]: numberValue,
        [result.name]: resultAmount || ''
      });
    } else if (resultAmount > 10000000000000) {
      setValues({ [tradeRate.name]: numberValue });
    }
  } else if (lockedLabel === result.lockedLabel) {
    const initialAmount = calculateTradeAmount(
      numberValue,
      result.amount,
      calculationRate,
      isDivision
    );
    setValues({
      [tradeRate.name]: numberValue,
      [initial.name]: initialAmount || ''
    });
  }
}

export function handleChangeThirdTertiaryComponent({
  lockedLabel,
  setValues,
  numberValue,
  initial,
  tradeRate,
  result,
  calculationRate,
  isDivision
}) {
  if (!lockedLabel) {
    setValues({ [result.name]: numberValue });
  } else if (
    lockedLabel === initial.lockedLabel ||
    (lockedLabel === result.lockedLabel && initial.amount)
  ) {
    const rateAmount = calculateTradeRate(
      initial.amount,
      numberValue,
      calculationRate,
      isDivision
    );
    setValues({
      [result.name]: numberValue,
      [tradeRate.name]: rateAmount || ''
    });
  } else if (lockedLabel === tradeRate.lockedLabel) {
    const initialAmount = calculateTradeAmount(
      tradeRate.amount,
      numberValue,
      calculationRate,
      isDivision
    );
    setValues({
      [result.name]: numberValue,
      [initial.name]: initialAmount || ''
    });
  }
}

// ***********************************************************************
//
// utility functions
//
// ***********************************************************************

// remove elements that desired fields was empty in components like phoneNumber or accounts
export function clearExtendableArray(array, conditions) {
  if (!array || array.length === 0) return array;
  const newArray = array.slice();

  for (let index = array.length - 1; index >= 0; index--) {
    const element = array[index];
    if (conditions) {
      if (conditions.or?.length) {
        for (const field of conditions.or) {
          if (field.length) {
            if (element[field.name].length === field.is) {
              newArray.splice(index, 1);
              break;
            }
          } else if (element[field.name] === field.is) {
            newArray.splice(index, 1);
            break;
          }
        }
      } else if (conditions.and?.length) {
        for (const field of conditions.and) {
          if (field.length) {
            if (element[field.name].length !== field.is) {
              break;
            }
          } else if (element[field.name] !== field.is) {
            break;
          }
          if (field === conditions.and[conditions.and.length - 1]) {
            newArray.splice(index, 1);
          }
        }
      }
    } else if (!element && element !== 0) {
      newArray.splice(index, 1);
    }
  }
  return newArray;
}

export function formatQueryString(queryStringsList) {
  let formattedData = '';

  if (!queryStringsList?.length) return '';
  const clearQueryStringsList = queryStringsList.filter((obj) => obj.value);
  const formattedQueryStringsList = clearQueryStringsList.map(
    (item) => `${item.key}=${item.value}`
  );

  formattedData = formattedQueryStringsList.join('&');

  return formattedData ? `?${formattedData}` : '';
}

export function getQueryStringVariable(variable) {
  const query = getQueryStringsFromLocation();
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return null;
}

export function createNewReportTab(path, queryStringsArray = []) {
  if (!path) return;
  const queryStrings = formatQueryString(queryStringsArray);
  window.location.href = `${
    window.paths.rptf || window.paths.rptw
  }${path}${queryStrings}`;
}

export function clearGroupCodesToSend(groupCodes) {
  const ALL_GROUP_CODE = -1;
  if (groupCodes.indexOf(ALL_GROUP_CODE) !== -1) {
    return [];
  }
  return groupCodes;
}

export function parseAccessToken(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// eslint-disable-next-line no-shadow
export function getInfoFromUrl(navItems, url) {
  const pages = navItems.map((navItem) => navItem.pages).flat(1);
  const pagesInfo = pages
    .map((page) =>
      page.children
        ? page.children.map((elem) => ({ href: elem.href, title: elem.title }))
        : { href: page.href, title: page.title }
    )
    .flat(1);
  const desiredPageInfo = pagesInfo.find((page) => page.href === url);
  return desiredPageInfo;
}

export function dataURLtoFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export async function takeScreenShot() {
  const screenshot = document.getElementById('root').firstChild;

  const canvas = await html2canvas(screenshot);
  const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
  const file = dataURLtoFile(dataUrl, 'bugScreenshot.jpg');
  return file;
}

export function assignInitialValue(initialValues, key, defaultValue) {
  return key in initialValues ? initialValues[key] : defaultValue;
}

// ***********************************************************************
//
// transaction description parser
//
// ***********************************************************************

export function formatDateTime(
  date,
  isGregorian = false,
  timeZone = 'Asia/Tehran',
  isDigitsLatin = true
) {
  const formattedDate = getTimeZoneFormattedDate(
    date,
    isGregorian,
    timeZone,
    isDigitsLatin
  );
  const locale = isGregorian ? 'en-US' : 'fa-IR';
  const numberingSystem = isDigitsLatin ? 'latn' : undefined;
  const formattedTime = new Date(+date).toLocaleTimeString(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    numberingSystem
  });
  return `${formattedDate} - ${formattedTime}`;
}

function getConditionalHookResult(text, hookValue) {
  const pureText = text.substr(1, text.length - 2);
  const value = pureText.split('|')[1];
  return value.split('//')[+hookValue];
}

function parseSinglePrefixes(prefixes, hookValue, matchedValue, translate, t) {
  if (prefixes.length === 0) return hookValue;

  let finalText = hookValue;

  // todo: throw exception when exception occurred!

  if (prefixes.includes('!')) {
    finalText = translate(finalText);
  }
  if (prefixes.includes('$')) {
    const decimalPlaces = getDecimalPlaces(hookValue);
    finalText = formatMoneyAmount(finalText, decimalPlaces);
  }
  if (prefixes.includes('*')) {
    finalText = getTimeZoneFormattedTime(finalText);
  }
  if (prefixes.includes('@')) {
    finalText = getTimeZoneFormattedDate(finalText);
  }
  if (prefixes.includes('?')) {
    finalText = finalText || t('Unknown');
  }
  if (prefixes.includes('&')) {
    finalText = finalText ? `(${finalText})` : '';
  }
  if (prefixes.includes('%')) {
    finalText = getConditionalHookResult(matchedValue, hookValue);
  }
  return finalText;
}

function getDecimalPlaces(matchedValue) {
  const decimalRegex = /\.\d+/;
  const decimalMatches = matchedValue.match(decimalRegex);
  if (decimalMatches) {
    return decimalMatches[0].length - 1;
  }
  return 0;
}

function getParsedHookValue(matchedValue, hookName, hookValue, translate, t) {
  if (!hookValue) return '';
  const prefixes = matchedValue.substr(1, matchedValue.indexOf(hookName) - 1);
  if (matchedValue.includes('#')) {
    return []
      .concat(hookValue)
      .map((elem) =>
        parseSinglePrefixes(prefixes, elem, matchedValue, translate, t)
      )
      .join(`${t('Comma')} `);
  }
  return parseSinglePrefixes(prefixes, hookValue, matchedValue, t);
}

function checkForRecursiveTemplates(template, metadata, translate, t) {
  const DEFAULT_HOOK_VALUES = {
    'amount-commission': '0',
    'currency-commission': ''
  };

  const regex = /<\{[^>]*\}>/g;
  const matchedArray = template.match(regex);

  if (!matchedArray) {
    return [template, metadata];
  }

  const recursiveTemplate = matchedArray[0];

  const hookDetectorRegex = /\{[^}]+\}/g;
  const hooksArray =
    recursiveTemplate
      .slice(2, -2)
      .match(hookDetectorRegex)
      ?.map((hook) => hook.match(/[-a-zA-Z]+/)[0]) || [];

  // ✅ inject default hooks
  const enrichedMetadata = [...metadata];
  hooksArray.forEach((hook) => {
    if (
      !enrichedMetadata.some((m) => m.hook === hook) &&
      hook in DEFAULT_HOOK_VALUES
    ) {
      enrichedMetadata.push({
        hook,
        value: DEFAULT_HOOK_VALUES[hook]
      });
    }
  });

  const validHooksArray = hooksArray.filter((hook) =>
    enrichedMetadata.some((m) => m.hook === hook)
  );

  if (!validHooksArray.length) {
    return [template.replace(regex, ''), enrichedMetadata];
  }

  const desiredMetadata = validHooksArray.map((hookName) => ({
    [hookName]: enrichedMetadata.filter((m) => m.hook === hookName)
  }));

  const iterationCount =
    Math.min(...desiredMetadata.map((obj) => Object.values(obj)[0].length)) ||
    1;

  const parsedIterations = Array(iterationCount).fill(recursiveTemplate);

  for (let i = 0; i < iterationCount; i++) {
    const currentMetadata = desiredMetadata
      .map((obj) => Object.values(obj)[0][i])
      .filter(Boolean);

    currentMetadata.forEach((hookObj) => {
      const innerRegex = new RegExp(
        `\\{[^(\\{\\}a-zA-Z)]*${hookObj.hook}(\\|[^\\}]*)?\\}`,
        'g'
      );

      parsedIterations[i] = parsedIterations[i].replace(innerRegex, (matched) =>
        getParsedHookValue(matched, hookObj.hook, hookObj.value, translate, t)
      );
    });
  }

  const parsedTemplate = parsedIterations
    .map((p) => p.slice(2, -2))
    .join(`${t('Comma')} `);

  const remainingMetadata = enrichedMetadata.filter(
    (obj) => !hooksArray.includes(obj.hook)
  );

  return checkForRecursiveTemplates(
    template.replace(regex, parsedTemplate),
    remainingMetadata,
    translate,
    t
  );
}

export function useParseDescriptionTemplate(
  templates,
  translate,
  tId,
  metadata
) {
  const { t } = useFrameworkTranslation();
  if (!templates?.length || !tId) return '';
  const parsedTemplate = templates.find((obj) => obj.id === tId);
  let parsedDescription = parsedTemplate?.body;
  if (!parsedTemplate) return `No related template found with ${tId}`;

  let remainingMetadata = metadata;

  [parsedDescription, remainingMetadata] = checkForRecursiveTemplates(
    parsedDescription,
    metadata,
    translate,
    t
  );

  const clearMetadata = [];
  remainingMetadata.forEach((hookObj) => {
    const hookIndex = clearMetadata.findIndex(
      (obj) => obj.hook === hookObj.hook
    );
    if (hookIndex === -1) {
      clearMetadata.push({ ...hookObj });
    } else {
      clearMetadata[hookIndex].value = []
        .concat(clearMetadata[hookIndex].value)
        .concat(hookObj.value);
    }
  });

  clearMetadata.forEach((hookObj) => {
    const regexStr = `\\{[^(\\}a-zA-Z)]*${hookObj.hook}(\\|[^\\}]*)?\\}`;
    const regex = new RegExp(regexStr, 'g');
    if (parsedDescription.match(regex)) {
      const matchedString = parsedDescription.match(regex)[0];
      parsedDescription = parsedDescription.replace(
        regex,
        getParsedHookValue(matchedString, hookObj.hook, hookObj.value, t, t)
      );
    }
  });
  return parsedDescription.trim();
}

// Non-hook version for use in render functions
export function parseDescriptionTemplate(templates, translate, tId, metadata) {
  if (!templates?.length || !tId) return '';
  const parsedTemplate = templates.find((obj) => obj.id === tId);
  let parsedDescription = parsedTemplate?.body;
  if (!parsedTemplate) return `No related template found with ${tId}`;

  let remainingMetadata = metadata;

  [parsedDescription, remainingMetadata] = checkForRecursiveTemplates(
    parsedDescription,
    metadata,
    translate,
    translate
  );

  const clearMetadata = [];
  remainingMetadata.forEach((hookObj) => {
    const hookIndex = clearMetadata.findIndex(
      (obj) => obj.hook === hookObj.hook
    );
    if (hookIndex === -1) {
      clearMetadata.push({ ...hookObj });
    } else {
      clearMetadata[hookIndex].value = []
        .concat(clearMetadata[hookIndex].value)
        .concat(hookObj.value);
    }
  });

  clearMetadata.forEach((hookObj) => {
    const regexStr = `\\{[^(\\}a-zA-Z)]*${hookObj.hook}(\\|[^\\}]*)?\\}`;
    const regex = new RegExp(regexStr, 'g');
    if (parsedDescription.match(regex)) {
      const matchedString = parsedDescription.match(regex)[0];
      parsedDescription = parsedDescription.replace(
        regex,
        getParsedHookValue(
          matchedString,
          hookObj.hook,
          hookObj.value,
          translate,
          translate
        )
      );
    }
  });
  return parsedDescription.trim();
}

export function deepCompare(obj1, obj2) {
  // Check if both objects are null or undefined
  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  // Check if both objects are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepCompare(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  // Check if both objects are objects
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }
    return true;
  }

  // Check if both objects are primitive types
  return obj1 === obj2;
}

export const convertPersianDigitsToEnglish = (text) => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  let convertedString = text;
  for (let i = 0; i < persianDigits.length; i++) {
    convertedString = convertedString.replace(
      new RegExp(persianDigits[i], 'g'),
      englishDigits[i]
    );
    convertedString = convertedString.replace(
      new RegExp(arabicDigits[i], 'g'),
      englishDigits[i]
    );
  }

  return convertedString;
};

// user can not type any character instead of number
export const numberTypeAccepter = (character) => {
  const NUMERIC_REGEX = /^[\u0660-\u0669\u06F0-\u06F90-9]+$/;
  let characters;

  if (character === '') {
    characters = '';
  } else if (NUMERIC_REGEX.test(character)) {
    characters = character;
  }

  return characters;
};

export const ShareToWhatsApp = async (sendText, phoneNumber) => {
  let whatsappUrl;

  const encodedText = encodeURIComponent(sendText);

  if (phoneNumber) {
    whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodedText}`;
  } else {
    whatsappUrl = `whatsapp://send?text=${encodedText}`;
  }
  openNewWindow(whatsappUrl);
};

export function getFormattedDate(isGregorian) {
  if (isGregorian) {
    return moment().format('YYYY-MM-DD');
  }
  return moment().locale('fa').format('jYYYY/MM/DD');
}

export const CustomerListExcelFileErrorGenerator = async (
  cellError,
  rowNumber,
  columnValue,
  errors,
  t
) => {
  const errorMessage = t('utils.invalidValueInRow', {
    cellError,
    rowNumber: rowNumber - 13,
    columnValue
  });
  errors.push(errorMessage);
};

export const CustomerListExcelFileSameAccountNumberErrorGenerator = async (
  columnTitle,
  accountNumber,
  errors,
  t
) => {
  const errorMessage = t('utils.accountCannotHaveDifferentValues', {
    accountNumber,
    columnTitle
  });
  errors.push(errorMessage);
};

export const arrayToJson = (data, opts = ['code', 'title']) => {
  const [key, value] = opts;
  const JsonObj = {};
  if (!data) {
    throw new Error(
      'your incoming object which is going to convert, is null or empty!'
    );
  }
  data?.forEach((item) => {
    JsonObj[item[key]] = item[value];
  });

  return JsonObj;
};

let timeout = null;
export const debounce = (callback, delay = 300) => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    callback();
  }, delay);
};

export function hasDuplicatePhoneNumber(phoneNumbers, item) {
  return (
    phoneNumbers.filter(({ phoneNumber }) => phoneNumber === item).length > 1
  );
}

export function navigateNewTab(address, data) {
  const key = crypto.randomUUID();

  sessionStorage.setItem(key, JSON.stringify(data));

  const url = new URL(address, window.location.href);
  url.searchParams.set('stateKey', key);

  const newTab = window.open(url.toString(), '_blank');

  if (newTab) {
    const timer = setInterval(() => {
      try {
        if (newTab.location.href.includes(address)) {
          newTab.history.replaceState(data, '', url.toString());
          clearInterval(timer);
        }
      } catch {
        //
      }
    }, 50);
  }
}

export function navigateSameTab(address, data) {
  const absoluteUrl = new URL(address, window.location.href).toString();
  window.history.pushState(data, '', absoluteUrl);
  window.location.href = absoluteUrl;
}

export const round = (value, roundation = 0) => {
  const roundedValue = Math.round(value * 10 ** -roundation) * 10 ** roundation;
  return roundedValue.toFixed(Math.max(0, roundation * -1));
};

export const remToPx = (rem) =>
  rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

export function generateReceiptText(
  receiptData,
  paymentOrderData,
  showCurrency
) {
  const amount = formatMoneyAmount(receiptData.amount);
  const currency = showCurrency({ code: paymentOrderData.currencyCode });
  const letterAmount = Num2persian(amount);
  const finalText = `
      ${letterAmount} ${currency}
      نام صاحب کارت:${paymentOrderData.accountHolderName} 

      شماره کارت: ${paymentOrderData.cardNumber} 
 
      شماره حساب: ${paymentOrderData.accountNumber} 

      شماره شبا: ${paymentOrderData.shebaNumber}

      نام بانک:${paymentOrderData.bankName} 

      مبلغ:${amount} ${currency} 

      در شرح واریز فقط هزینه عمومی یا خرید خدمات نوشته کنید 
واریزی هایی که با شرح واریز حقوق انجام بدهید به حساب مبدا برگشت داده میشود       

       لطفا دقت شود فقط و فقط ${amount} ${currency} به این حساب انتقال دهید.
        انتقال مبلغ بیشتر مجرا داده نخواهد شد`;
  return finalText;
}

export const getTranslatedLanguage = (langKey, t) => {
  switch (langKey) {
    case 'en':
      return t('English');
    case 'fa':
      return t('Persian');
    case 'fad':
      return t('Dari');
    case 'fap':
      return t('Pashto');
    default:
      return null;
  }
};

export const getFirstDashboardItem = (role, appType) => {
  const safeRole = typeof role === 'string' ? role.toLowerCase() : '';
  const safeAppType = typeof appType === 'string' ? appType.toLowerCase() : '';

  const apptypeWithSpecialFallback = 'topupexchanger';

  const hasPermission = (key) => {
    const checkFn = permissionFunctions[key];

    if (!checkFn) return false;

    const appTypeFirstFns = [
      'IsAfghanVersion',
      'IsSupportUser',
      'ChequesListA',
      'IsBasicBranch'
    ];

    if (appTypeFirstFns.includes(key)) {
      return checkFn(safeAppType);
    }
    return checkFn(safeRole, safeAppType);
  };

  const hasBiDashboardAccess =
    permissionFunctions.ProfitAndLossStatement?.(
      safeRole,
      safeAppType
    );

  if (hasBiDashboardAccess) {
    return {
      href: '/dashboard/bi-dashboard'
    };
  }

  if (safeAppType === apptypeWithSpecialFallback) {
    return {
      href: '/dashboard/credit-account'
    };
  }

  for (const section of navItems) {
    if (!section.pages) continue;

    for (const page of section.pages) {
      let hasParentAccess = true;
      if (page.authKeys && page.authKeys.length > 0) {
        hasParentAccess = page.authKeys.every(hasPermission);
      }

      if (!hasParentAccess) continue;

      if (page.children && page.children.length > 0) {
        for (const child of page.children) {
          if (!child.authKeys || child.authKeys.length === 0) {
            return child;
          }

          const hasChildAccess = child.authKeys.every(hasPermission);

          if (hasChildAccess) {
            return child;
          }
        }
      } else {
        return page;
      }
    }
  }

  return null;
};
export const getCommunicationInputValue = (value, isTelegramEnabled, isDisplay) => {
  const rawValue = value || '';
  if (!rawValue) return isDisplay ? '-' : '';

  const isWhatsAppLink = /^https:\/\/(chat|wa)\.whatsapp\.com\//i.test(
    rawValue.trim()
  );
  const isTelegramChatId = /^-?\d{5,20}$/.test(rawValue.trim());

  // If Telegram is active and the stored value is a WhatsApp link -> don't show it
  if (isTelegramEnabled && isWhatsAppLink) {
    return isDisplay ? '-' : '';
  }

  // If WhatsApp is active and the stored value is a Telegram Chat ID -> don't show it
  if (!isTelegramEnabled && isTelegramChatId) {
    return isDisplay ? '-' : '';
  }

  return rawValue;
};

export const sortAccountStatus = (data) =>
  data ? [...data].sort((a, b) => {
    const getRank = (item) => {
      if (item.isDebtor && item.balance === 0) return 3;
      if (item.isDebtor) return 1;
      return 2;
    };

    const rankA = getRank(a);
    const rankB = getRank(b);

    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return b.balance - a.balance;
  }) : [];
